import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { CalendarRange } from 'lucide-react'
import type { DateRange } from 'react-day-picker'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  DASHBOARD_WINDOWS,
  DEFAULT_DASHBOARD_WINDOW,
  customTimeWindow,
  dashboardTimeWindow,
  validateCustomRange,
  type DashboardTimeWindow,
  type DashboardWindowId,
} from '@/lib/dashboardLayouts'
import { windowLabel } from '@/lib/reportRange'
import { addDays, isoDay, parseIsoDay } from '@/lib/taskDateRange'
import { cn } from '@/lib/utils'

type Period = { kind: 'preset'; id: Exclude<DashboardWindowId, 'custom'> } | { kind: 'custom'; from: string; to: string }

type DashboardPeriodValue = {
  time: DashboardTimeWindow
  setWindow: (id: Exclude<DashboardWindowId, 'custom'>) => void
  /** Both ends inclusive ISO days; callers validate with `validateCustomRange`. */
  setCustom: (from: string, to: string) => void
}

const DashboardPeriodContext = createContext<DashboardPeriodValue | null>(null)

/** The one period every widget on a dashboard reads: a preset, or a custom from–to. */
export function DashboardPeriodProvider({
  initial = DEFAULT_DASHBOARD_WINDOW as Exclude<DashboardWindowId, 'custom'>,
  children,
}: {
  initial?: Exclude<DashboardWindowId, 'custom'>
  children: ReactNode
}) {
  const [period, setPeriod] = useState<Period>({ kind: 'preset', id: initial })
  // Today is read once per choice, not per render: a dashboard left open past
  // midnight keeps the window it was showing until the user picks again.
  const time = useMemo(
    () => (period.kind === 'custom' ? customTimeWindow(period.from, period.to) : dashboardTimeWindow(period.id, new Date())),
    [period],
  )
  return (
    <DashboardPeriodContext.Provider
      value={{
        time,
        setWindow: id => setPeriod({ kind: 'preset', id }),
        setCustom: (from, to) => setPeriod({ kind: 'custom', from, to }),
      }}
    >
      {children}
    </DashboardPeriodContext.Provider>
  )
}

export function useDashboardPeriod(): DashboardPeriodValue {
  const value = useContext(DashboardPeriodContext)
  if (!value) throw new Error('useDashboardPeriod must be used inside DashboardPeriodProvider')
  return value
}

/**
 * Today / 7 / 30 / 90 days, or Custom: a from–to picked on a two-month range
 * calendar (with quick picks), applied to every widget at once.
 */
export function DashboardPeriodSelect({ className }: { className?: string }) {
  const { time, setWindow, setCustom } = useDashboardPeriod()
  const custom = time.id === 'custom'
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <div role='group' aria-label='Period' className='flex rounded-full bg-gray-100 p-0.5'>
        {DASHBOARD_WINDOWS.map(w => (
          <button
            key={w.id}
            type='button'
            aria-pressed={time.id === w.id}
            onClick={() => setWindow(w.id as Exclude<DashboardWindowId, 'custom'>)}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium transition-colors',
              time.id === w.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
            )}
          >
            {w.label}
          </button>
        ))}
        <CustomRangePicker
          active={custom}
          current={time.window}
          onApply={(from, to) => setCustom(from, to)}
        />
      </div>
      {!custom && <span className='text-xs text-gray-400'>{windowLabel(time.window)}</span>}
    </div>
  )
}

function CustomRangePicker({
  active,
  current,
  onApply,
}: {
  active: boolean
  current: { from: string; to: string }
  onApply: (from: string, to: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [range, setRange] = useState<DateRange | undefined>()
  const today = new Date()
  const from = range?.from ? isoDay(range.from) : ''
  const to = range?.to ? isoDay(range.to) : range?.from ? isoDay(range.from) : ''
  const error = from ? validateCustomRange(from, to, today) : null

  const openPicker = (next: boolean) => {
    if (next) setRange({ from: parseIsoDay(current.from), to: parseIsoDay(current.to) })
    setOpen(next)
  }
  const quick = (f: Date, t: Date) => setRange({ from: f, to: t })
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const quickPicks: { label: string; from: Date; to: Date }[] = [
    { label: 'This month', from: startOfMonth, to: today },
    {
      label: 'Last month',
      from: new Date(today.getFullYear(), today.getMonth() - 1, 1),
      to: new Date(today.getFullYear(), today.getMonth(), 0),
    },
    { label: 'Last 180 days', from: parseIsoDay(addDays(isoDay(today), -179)), to: today },
    { label: 'Year to date', from: new Date(today.getFullYear(), 0, 1), to: today },
  ]

  return (
    <Popover open={open} onOpenChange={openPicker}>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-pressed={active}
          aria-label='Custom date range'
          className={cn(
            'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors',
            active ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700',
          )}
        >
          <CalendarRange className='h-3.5 w-3.5' />
          {active ? windowLabel(current) : 'Custom'}
        </button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-auto p-0'>
        <div className='flex'>
          <div className='flex w-36 flex-col gap-0.5 border-r border-gray-100 p-2'>
            <p className='px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400'>Quick pick</p>
            {quickPicks.map(q => (
              <button
                key={q.label}
                type='button'
                onClick={() => quick(q.from, q.to)}
                className='rounded-md px-2 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-100'
              >
                {q.label}
              </button>
            ))}
          </div>
          <div className='p-3'>
            <Calendar
              mode='range'
              numberOfMonths={2}
              selected={range}
              onSelect={setRange}
              defaultMonth={range?.from ?? new Date(today.getFullYear(), today.getMonth() - 1, 1)}
              disabled={{ after: today }}
              classNames={{
                range_middle: '[&>button]:!bg-blue-50 [&>button]:!text-blue-900 [&>button]:rounded-none',
                range_start: '[&>button]:rounded-r-none',
                range_end: '[&>button]:rounded-l-none',
              }}
            />
            <div className='mt-2 flex items-center justify-between gap-3 border-t border-gray-100 pt-3'>
              <p className={cn('text-xs', error ? 'text-red-600' : 'text-gray-500')}>
                {error ?? (from ? windowLabel({ from, to }) : 'Pick a start and an end date')}
              </p>
              <div className='flex gap-2'>
                <Button size='sm' variant='ghost' onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  size='sm'
                  disabled={!from || !!error}
                  onClick={() => {
                    onApply(from, to)
                    setOpen(false)
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
